import { Injectable, inject, signal } from '@angular/core';
import { RefreshService } from './refresh-service';
import { UserService } from '../generated/backend';

/**
 * Single source of truth for the current user's unread notification count.
 *
 * Extends {@link RefreshService} so any component can trigger a reload after a
 * mutation (project save, project edit, driver/OM notification, ...) via
 * {@link RefreshService.triggerRefresh}. The count is exposed as a signal for
 * the header badge.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationService extends RefreshService {
  private userService = inject(UserService);

  private readonly _count = signal(0);
  readonly count = this._count.asReadonly();

  constructor() {
    super();
    this.refresh$.subscribe(() => this.load());
  }

  /** Explicitly (re)load the notification count, e.g. on login / app init. */
  refresh(): void {
    this.triggerRefresh();
  }

  /** Reset the count to zero, e.g. on logout. */
  reset(): void {
    this._count.set(0);
  }

  private load(): void {
    this.userService.getUserNotificationCount().subscribe({
      next: dto => this._count.set(dto.count ?? 0),
      error: () => this._count.set(0),
    });
  }
}
